import React from 'react';
import { InputNumber, Slider } from 'antd';
import { useTranslation } from 'react-i18next';
import { Title } from './styles';

interface SliderWithNumericInputsProps {
  title: string;
  startValue?: number;
  endValue?: number;
  max: number;
  min?: number;
  onStartChange?: (value: number | undefined) => void;
  onEndChange?: (value: number) => void;
  onSliderChange?: (value: [number, number]) => void;
  formatter?: (value?: number) => string;
  startPlaceholder?: string;
  endPlaceholder?: string;
}

const clamp = (v: number, min: number, max: number) => Math.min(Math.max(v, min), max);

export const SliderWithNumericInputs: React.FC<SliderWithNumericInputsProps> = ({
  title,
  startValue,
  endValue,
  min = 0,
  max,
  startPlaceholder,
  endPlaceholder,
  formatter,
  onStartChange,
  onEndChange,
  onSliderChange,
}) => {
  const { t } = useTranslation();
  const startInputHandler = (e: number) => {
    const nextStart = clamp(e ?? 0, min, max);
    if (endValue !== undefined && nextStart > endValue) {
      onEndChange?.(nextStart);
    }
    return onStartChange ? onStartChange(nextStart) : null;
  };

  const endInputHandler = (e: number) => {
    const nextEnd = clamp(e ?? 0, min, max);
    if (startValue !== undefined && nextEnd < startValue) {
      onStartChange?.(nextEnd);
    }
    return onEndChange ? onEndChange(nextEnd) : null;
  };

  return (
    <div>
      <Title>{title}</Title>
      <div>
        <InputNumber<number>
          style={{ marginRight: 8 }}
          min={min}
          max={max}
          value={startValue}
          stringMode={false}
          placeholder={startPlaceholder || t('common.min')}
          onChange={startInputHandler}
          formatter={formatter}
        />
        <InputNumber<number>
          min={min}
          max={max}
          value={endValue}
          placeholder={endPlaceholder || t('common.max')}
          onChange={endInputHandler}
          formatter={formatter}
        />
      </div>
      <Slider min={min} max={max} value={[startValue || 0, endValue || 0]} onChange={onSliderChange} range />
    </div>
  );
};
